/**
 * 格式化货币显示
 * @param value 数值
 * @returns 格式化后的字符串
 */
export const formatCurrency = (value: number): string => {
  return value.toLocaleString('zh-CN');
};

/**
 * 防抖函数
 * @param func 要执行的函数
 * @param wait 等待时间（毫秒）
 * @returns 防抖处理后的函数
 */
export const debounce = <T extends (...args: any[]) => any>(
  func: T,
  wait: number
): ((...args: Parameters<T>) => void) => {
  let timeout: ReturnType<typeof setTimeout> | null = null;
  
  return function(...args: Parameters<T>): void {
    if (timeout) {
      clearTimeout(timeout);
    }
    
    timeout = setTimeout(() => {
      func(...args);
    }, wait);
  };
};

/**
 * 深拷贝对象
 * @param obj 要拷贝的对象
 * @returns 拷贝后的对象
 */
export const deepClone = <T>(obj: T): T => {
  if (obj === null || typeof obj !== 'object') {
    return obj;
  }
  
  if (obj instanceof Date) {
    return new Date(obj.getTime()) as unknown as T;
  }
  
  if (obj instanceof Array) {
    return obj.map(item => deepClone(item)) as unknown as T;
  }
  
  if (obj instanceof Object) {
    const copy = {} as Record<string, any>;
    Object.keys(obj).forEach(key => {
      copy[key] = deepClone((obj as Record<string, any>)[key]);
    });
    return copy as T;
  }
  
  throw new Error(`Unable to copy obj! Its type isn't supported.`);
};

/**
 * 判断对象是否为空
 * @param obj 要判断的对象
 * @returns 是否为空
 */
export const isEmpty = (obj: any): boolean => {
  if (obj === null || obj === undefined) {
    return true;
  }
  
  if (typeof obj === 'string' || Array.isArray(obj)) {
    return obj.length === 0;
  }
  
  if (typeof obj === 'object') {
    return Object.keys(obj).length === 0;
  }
  
  return false;
};

/**
 * 获取URL参数
 * @param name 参数名
 * @returns 参数值
 */
export const getUrlParam = (name: string): string | null => {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get(name);
};

/**
 * 生成唯一ID
 * @returns 唯一ID
 */
export const generateUniqueId = (): string => {
  return Date.now().toString(36) + Math.random().toString(36).substring(2);
};

/**
 * 省份按拼音首字母排序的映射表（与Excel排序一致）
 */
export const provinceFirstLetterMap: Record<string, string> = {
  '安徽省': 'A',
  '北京市': 'B',
  '重庆市': 'C',
  '福建省': 'F',
  '甘肃省': 'G',
  '广东省': 'G',
  '广西壮族自治区': 'G',
  '贵州省': 'G',
  '海南省': 'H',
  '河北省': 'H',
  '河南省': 'H',
  '黑龙江省': 'H',
  '湖北省': 'H',
  '湖南省': 'H',
  '吉林省': 'J',
  '江苏省': 'J',
  '江西省': 'J',
  '辽宁省': 'L',
  '内蒙古自治区': 'N',
  '宁夏回族自治区': 'N',
  '青海省': 'Q',
  '山东省': 'S',
  '山西省': 'S',
  '陕西省': 'S',
  '上海市': 'S',
  '四川省': 'S',
  '天津市': 'T',
  '西藏自治区': 'X',
  '新疆维吾尔自治区': 'X',
  '云南省': 'Y',
  '浙江省': 'Z'
};

/**
 * 按拼音首字母对省份数组进行排序
 * @param provinces 省份数组
 * @returns 排序后的省份数组
 */
export const sortProvincesByFirstLetter = (provinces: string[]): string[] => {
  return [...provinces].sort((a, b) => {
    // 获取省份的拼音首字母
    const letterA = provinceFirstLetterMap[a] || a;
    const letterB = provinceFirstLetterMap[b] || b;
    
    // 先按首字母排序
    if (letterA !== letterB) {
      return letterA.localeCompare(letterB);
    }
    
    // 如果首字母相同，则按原始字符串排序
    return a.localeCompare(b);
  });
}; 